import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import SimpleBarChart from 'components/pages/professor/statistics/SimpleBarChart';
import LineChart from 'components/pages/professor/statistics/LineChart';
import TableForm from 'components/common/lists/TableForm';
import tableStyles from 'styles/components/common/lists/Table.module.scss';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/chevron_right.svg';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import { TTableColumn } from 'types/components/table';
import { TScore } from 'types/chart';
import { TAxiosResponse } from 'types/apis/api';
import {
  TStatisticsByStudentResponse,
  TStudentChartScoreList,
} from 'types/apis/professor';
import { getStatisticsByStudent } from 'apis/professor';
import useModal from 'hooks/useModal';

type TProps = {
  courseId: number | undefined;
  courseInfoId: number | undefined;
  chartRound: number | undefined;
  studentUserId: number | undefined;
  className: string;
  studentName: string;
};

const StatisticsByStudent = ({
  courseId,
  courseInfoId,
  chartRound,
  studentUserId,
  className,
  studentName,
}: TProps) => {
  const navigate = useNavigate();

  const { openModal } = useModal();

  const [average, setAverage] = useState<TScore[]>([
    {
      label: '-',
      score: null,
    },
    {
      label: '-',
      score: null,
    },
    {
      label: '-',
      score: null,
    },
  ]);
  const [roundData, setRoundData] = useState<TScore[]>([]);

  useEffect(() => {
    if (courseInfoId && chartRound && studentUserId) return;
    setAverage([
      { label: studentName, score: null },
      { label: `${className}반`, score: null },
      { label: '전체', score: null },
    ]);
  }, [className, studentName]);

  const { data: statisticsByStudentData } = useQuery(
    ['getStatisticsByRound', courseInfoId, chartRound, studentUserId],
    () => {
      if (!courseInfoId || !chartRound || !studentUserId) return;
      return getStatisticsByStudent({
        courseInfoId,
        chartRound,
        studentUserId,
      });
    },
    {
      enabled: !!courseInfoId && !!chartRound && !!className && !!studentName,
      onSuccess: ({ data }: TAxiosResponse<TStatisticsByStudentResponse>) => {
        if (!data) return;
        // 학생 종합 평가 점수
        setAverage([
          {
            label: `${studentName.substring(0, 10)}${
              studentName.length > 10 ? '...' : ''
            }`,
            score:
              data.averageDTO?.studentAverage !== null
                ? data.averageDTO.studentAverage
                : null,
          },
          {
            label: `${className}반`,
            score:
              data.averageDTO?.classAverage !== null
                ? data.averageDTO.classAverage
                : null,
          },
          {
            label: '전체',
            score:
              data.averageDTO?.allAverage !== null
                ? data.averageDTO.allAverage
                : null,
          },
        ]);
        // 성적 변화 추이
        setRoundData(
          data.chartRoundGraphList?.map((item) => ({
            label: item.chartRound.toString(),
            score: item.averageScore !== null ? item.averageScore : null,
          }))
        );
      },
    }
  );

  // 차트별 점수 상세
  const tableColumns: TTableColumn[] = [
    { name: 'rank', label: '순위', width: '102px' },
    { name: 'chartTypeName', label: '차트 종류', width: '250px' },
    {
      name: 'classAverage',
      label: `${className}반 평균`,
      width: '200px',
    },
    { name: '', label: '평가결과', width: '268px' },
    { name: '', label: '자세히 보기', width: '200px' },
  ];

  const tableData: TStudentChartScoreList[] =
    statisticsByStudentData?.data?.studentChartScoreList || [];

  const renderActions = (row: TStudentChartScoreList, headerIndex?: number) => (
    <>
      {headerIndex === 3 && (
        <div className={tableStyles.difference}>
          <p>{row.studentAverage}</p>
          <span
            className={
              tableStyles[
                row.difference > 0 ? 'up' : row.difference < 0 ? 'down' : 'even'
              ]
            }
          >
            {row.difference ? row.differenceScore : ''}
          </span>
        </div>
      )}
      {headerIndex === 4 && (
        <button
          type="button"
          className={tableStyles.link}
          onClick={() => {
            const chartRoute = ChartInfo.find(
              (chart) => chart.type === row.chartType
            )?.route;
            if (
              !chartRoute ||
              !row.evaluationChartId ||
              !courseId ||
              !courseInfoId ||
              !chartRound ||
              !studentUserId
            ) {
              openModal({
                title: '차트 조회 실패',
                text: '관리자에게 문의해 주세요.',
              });
              return;
            }
            sessionStorage.setItem('route', 'statistics');
            navigate(
              `/feedback${chartRoute}/${
                row.evaluationChartId
              }?courseId=${courseId}&courseInfoId=${courseInfoId}&chartRound=${chartRound}&studentUserId=${studentUserId}&tab=${
                row.evaluationRound === 2 ? 2 : 0
              }`
            );
          }}
          disabled={!row.evaluationChartId}
        >
          차트 상세보기
          <ChevronRightIcon
            width={20}
            height={20}
            fill={
              STYLES_VARIABLES[!row.evaluationChartId ? 'GRAY_05' : 'PURPLE_01']
            }
          />
        </button>
      )}
    </>
  );

  return (
    <>
      {/* 학생 종합 평가 점수 */}
      <SimpleBarChart data={average} />

      {/* 성적 변화 추이 */}
      <LineChart data={roundData} />

      {/* 차트별 점수 상세 */}
      <div className="mt40">
        <h2 className="mb24 main_title">차트별 점수 상세</h2>
        <TableForm
          data={tableData}
          columns={tableColumns}
          renderActions={renderActions}
          emptyMessage="등록된 차트가 없습니다."
        />
      </div>
    </>
  );
};
export default StatisticsByStudent;
